import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SURFACES, ACCENTS, LOOKS, getDesignLab, applyDesignLab } from '../utils/themeResolver';
import { applyDesignV2 } from '../utils/designV2';
import { updateSettings } from '../redux/slices/settingsSlice';

// Pre-release design lab: switch the panel surface and accent palette in the
// live app. Dev builds only (or localStorage 'mixgoat-design-lab' present).
// Toggle with Cmd/Ctrl+Shift+D. Not user-facing, no analytics.
const enabled = process.env.NODE_ENV !== 'production'
    || (typeof localStorage !== 'undefined' && !!localStorage.getItem('mixgoat-design-lab'));

export default function DesignLab() {
    const [open, setOpen] = useState(false);
    const [lab, setLab] = useState(getDesignLab);
    const dispatch = useDispatch();
    const designV2 = useSelector((st) => !!st.settings?.designV2);
    const toggleDesignV2 = () => {
        const next = !designV2;
        applyDesignV2(next);
        dispatch(updateSettings({ designV2: next }));
    };

    useEffect(() => {
        if (!enabled) return undefined;
        applyDesignLab(lab, { silent: true });
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'd') {
                e.preventDefault();
                setOpen((o) => !o);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!enabled || !open) return null;

    const pick = (patch) => {
        const next = { ...lab, ...patch };
        if (patch.surface || patch.accent) delete next.look;
        setLab(next);
        applyDesignLab(next);
    };

    const Btn = ({ on, onClick, children }) => (
        <button
            type="button"
            onClick={onClick}
            style={{
                border: 0, cursor: 'pointer', borderRadius: 999, padding: '6px 10px', fontSize: 12, fontWeight: 600,
                background: on ? 'var(--color-accent)' : 'rgba(255,255,255,0.08)',
                color: on ? '#061316' : 'var(--color-text-primary)',
            }}
        >
            {children}
        </button>
    );

    return (
        <div
            style={{
                position: 'fixed', left: '50%', bottom: 100, transform: 'translateX(-50%)', zIndex: 99999,
                display: 'flex', gap: 6, alignItems: 'center', padding: 6, borderRadius: 999,
                background: 'rgba(20,20,20,0.9)', backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                fontFamily: 'Inter, sans-serif', flexWrap: 'wrap', maxWidth: '96vw', justifyContent: 'center',
            }}
        >
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', padding: '0 6px' }}>v2</span>
            <Btn on={designV2} onClick={toggleDesignV2}>{designV2 ? 'On' : 'Off'}</Btn>
            <span style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)', margin: '0 4px' }} />
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', padding: '0 6px' }}>Look</span>
            {Object.entries(LOOKS).map(([k, v]) => (
                <Btn key={k} on={lab.look === k} onClick={() => pick({ look: lab.look === k ? undefined : k })}>{v.label}</Btn>
            ))}
            <span style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)', margin: '0 4px' }} />
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', padding: '0 6px' }}>Surface</span>
            {Object.entries(SURFACES).map(([k, v]) => (
                <Btn key={k} on={lab.surface === k} onClick={() => pick({ surface: k })}>{v.label}</Btn>
            ))}
            <span style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)', margin: '0 4px' }} />
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', padding: '0 6px' }}>Accent</span>
            {Object.entries(ACCENTS).map(([k, v]) => (
                <Btn key={k} on={lab.accent === k} onClick={() => pick({ accent: k })}>{v.label}</Btn>
            ))}
            <Btn onClick={() => { setLab({}); applyDesignLab({}); localStorage.removeItem('mixgoat-design-lab'); window.location.reload(); }}>Reset</Btn>
        </div>
    );
}
